import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import { formatCurrency } from "../utils/format";
import { fxSettings } from "../utils/renderTier";
import { gaugeGeometry } from "../utils/gaugeGeometry";
import { heatColor, reachedColors } from "../utils/heatColor";
import { bigDealAmount } from "../constants";
import { chargeCellMs, chargeCells } from "./charge";
import { hornBlasts, isPlainAlert, voiceEndMs, voiceLineMs, voiceMs } from "./horn";
import { feedLandMs, feedPopMs } from "./targetFeed";
import { beatBarAt, personalBestMs } from "./recordBuild";
import { maxRows, missedSalesMs, rowDelayMs, totalAfterMs } from "./missedSales";
import {
  partyBlocksMs,
  partyCannonSpread,
  partyLightsMs,
  partyLineMs,
  partyLines,
  targetPartyMs,
} from "./party";
import {
  targetChargeMs,
  targetFlyMs,
  targetFullMs,
  targetSweepAt,
  targetSweepStartMs,
} from "./targetCharge";
import "./animations.css";

// De laag over het dashboard (pointer-events:none). Animaties van bestaande
// onderdelen lopen via een klasse op de dashboard-div. Het aantal deeltjes
// komt van de kwaliteitstrap (utils/renderTier.js).

const colors = ["#ffffff", "#ffd166", "#ff9a3c", "#7fd8ff", "#cfeaff"];

const spread = (count, build) => Array.from({ length: count }, (_, i) => build(i));

// Evenredig uitdunnen: elke zoveelste, zodat het over het beeld verdeeld blijft.
const thin = (list, keep) => {
  if (keep >= list.length) return list;
  const step = list.length / keep;
  return spread(keep, (i) => list[Math.floor(i * step)]);
};

// De aanloop: een rij blokjes loopt vol, met de tijden uit ./charge.js.
const chargeDelays = spread(chargeCells, chargeCellMs);

// Een stoot van de toeter, met de tijden uit ./horn.js; de staart laat de stralen uitwaaien.
const blastStyle = ([start, end]) => ({
  "--fx-blast-at": `${start}ms`,
  "--fx-blast-ms": `${end - start + 220}ms`,
});

// Een luchthoorn, naar rechts: bus, knop, ventiel en toeter.
const hornArt = (
  <svg className="fx-horn-art" viewBox="0 0 320 140" width="320" height="140">
    <rect x="44" y="16" width="30" height="16" rx="4" fill="#c9d1d9" />
    <rect x="0" y="30" width="124" height="80" rx="14" fill="#e5484d" />
    <rect x="0" y="54" width="124" height="14" fill="#b8323a" />
    <rect x="0" y="80" width="124" height="8" fill="#b8323a" />
    <rect x="122" y="58" width="28" height="24" fill="#c9d1d9" />
    <polygon points="148,60 300,12 300,128 148,80" fill="#ffd166" />
    <rect x="294" y="6" width="16" height="128" rx="6" fill="#f4b942" />
  </svg>
);

// De stralen uit de toeter, een bundel per stoot.
const hornBeams = hornBlasts.map((blast, i) => (
  <div key={i} className="fx-horn-blast" style={blastStyle(blast)}>
    <i />
    <i />
    <i />
  </div>
));

// Elke stoot slaat de toeter terug. Eén laag per stoot, in elkaar: transforms tellen op,
// dus een stoot die nog uitdeint loopt niet vast op de volgende.
const kickedHorn = hornBlasts.reduceRight(
  (inner, blast, i) => (
    <div className="fx-horn-kick" style={blastStyle(blast)}>
      {inner}
    </div>
  ),
  <>
    {hornArt}
    {hornBeams}
  </>,
);

// Uit beide hoeken onderin.
const AirHorns = () => (
  <div className="fx-horns">
    <div className="fx-horn fx-horn-left">{kickedHorn}</div>
    <div className="fx-horn fx-horn-right">{kickedHorn}</div>
  </div>
);

// Per blokje hoe heet hij is: van goud naar wit-heet rood, zoals de meter.
const chargeHeat = chargeDelays.map((_, i) => heatColor((i / (chargeCells - 1)) * 100));

// Het percentage onder de blokjes: per stap een getal, zichtbaar tot het volgende blokje.
// De laatste (100%) blijft staan.
const chargeCount = [0, ...chargeDelays].map((start, i) => ({
  label: `${Math.round((i / chargeCells) * 100)}%`,
  style: {
    animationDelay: `${start}ms`,
    animationDuration: i < chargeCells ? `${chargeDelays[i] - start}ms` : "1ms",
    color: heatColor((i / chargeCells) * 100),
  },
  last: i === chargeCells,
}));

// Als de rij vol is spat hij uiteen: vlakke scherven naar buiten. width: hoe breed ze
// vertrekken (de hele rij, of bijna een punt bij de meter).
function chargeShards(big, width) {
  const count = fxSettings().chargeShards * (big ? 2 : 1);
  const reach = big ? 620 : 380;
  return spread(count, () => {
    const angle = Math.random() * Math.PI * 2;
    const distance = reach * (0.45 + Math.random() * 0.75);
    const size = 10 + Math.random() * (big ? 26 : 16);
    return {
      width: `${size}px`,
      height: `${size * (0.4 + Math.random() * 0.6)}px`,
      background: heatColor(35 + Math.random() * 65),
      animationDelay: `${Math.random() * 60}ms`,
      "--from": `${(Math.random() * 2 - 1) * width}px`,
      "--x": `${Math.cos(angle) * distance}px`,
      "--y": `${Math.sin(angle) * distance * 0.7 - 40}px`,
      "--spin": `${Math.random() * 720 - 360}deg`,
    };
  });
}

// Na de toeter episch (is-epic): balken als in de bioscoop, blokjes die heet worden met
// een teller eronder, de kaart die langzaam naar je toe komt, en op de flits een schok en
// scherven. Een grote deal (is-big) nog harder. Bij Oud de oude aanloop.
const SaleCharge = ({ amount = 0 }) => {
  const epic = !isPlainAlert();
  const big = epic && amount >= bigDealAmount;
  const className = ["fx-charge", epic && "is-epic", big && "is-big"].filter(Boolean).join(" ");

  return (
    <div className={className}>
      <div className="fx-charge-dim" />
      {epic && (
        <>
          <div className="fx-charge-bar is-top" />
          <div className="fx-charge-bar is-bottom" />
        </>
      )}
      <div className="fx-charge-shake">
        <div className="fx-charge-push">
          <div className="fx-charge-card">
            <div className="fx-charge-kicker">{big ? "Grote deal" : "Er komt iets binnen"}</div>
            <div className="fx-charge-cells">
              {chargeDelays.map((delay, i) => (
                <span key={i} className="fx-charge-cell">
                  <i
                    className="fx-charge-cell-on"
                    style={{
                      animationDelay: `${delay}ms`,
                      background: epic ? chargeHeat[i] : undefined,
                    }}
                  />
                </span>
              ))}
            </div>
            {epic && (
              <div className="fx-charge-count">
                {chargeCount.map((step) => (
                  <span key={step.label} className={step.last ? "is-last" : ""} style={step.style}>
                    {step.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="fx-charge-flash" />
    </div>
  );
};

// De aankondiging van een verkoop.
// ms: afwijkende duur (bij een record). handoff: de recordkaart neemt het over.
// best: { previous, at }, op `at` ms draait hij om tot eigen record (0: meteen).
const SaleSplash = ({ name, amount, service, ms, handoff, best }) => {
  const style = {};
  if (ms) style["--fx-splash-ms"] = `${ms}ms`;
  if (best) {
    style["--fx-best-at"] = `${best.at}ms`;
    style["--fx-beat-at"] = `${beatBarAt(best.at)}ms`;
  }
  const flip = Boolean(best && best.at > 0);
  const cardClass = [
    "fx-splash-card",
    handoff && "is-handoff",
    best && "is-best",
    flip && "is-flip",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="fx-splash" style={style}>
      <div className="fx-splash-dim" />
      <div className={cardClass}>
        <div className="fx-splash-rise">
          <div className="fx-splash-line fx-splash-kicker">
            {!best || flip ? <span className="fx-kick-sale">Nieuwe verkoop van</span> : null}
            {best ? <span className="fx-kick-best">Eigen record van</span> : null}
          </div>
          <div className="fx-splash-line fx-splash-name">{name}</div>
          <div className="fx-splash-line fx-splash-amount">€{formatCurrency(amount)}</div>
          {service ? (
            <div className="fx-splash-line fx-splash-service">{service}</div>
          ) : null}
          {best?.previous ? <BeatBar amount={amount} previous={best.previous} /> : null}
        </div>
      </div>
    </div>
  );
};

// Oud record gedimd, het verschil in goud; allebei altijd zichtbaar (15 tot 65%).
const BeatBar = ({ amount, previous }) => {
  const old = Math.min(65, Math.max(15, (previous / amount) * 100));
  return (
    <div className="fx-beat" style={{ "--fx-beat-old": `${old}%` }}>
      <div className="fx-beat-track">
        <div className="fx-beat-old" />
        <div className="fx-beat-new" />
      </div>
      <div className="fx-beat-mark" />
      <div className="fx-beat-was">was €{formatCurrency(previous)}</div>
      <div className="fx-beat-plus">+€{formatCurrency(amount - previous)}</div>
    </div>
  );
};

// Houd maxDelay + minDuration + durationSpread onder de `ms` in registry.js.
// count: deel van een volle bui; after: seconden wachten voor de eerste snipper.
const ConfettiBurst = ({
  count = 1,
  maxDelay = 1.6,
  minDuration = 2.6,
  durationSpread = 2,
  after = 0,
}) => {
  const bits = useMemo(
    () =>
      spread(Math.max(6, Math.round(fxSettings().confettiBits * count)), (i) => ({
        left: Math.random() * 100,
        delay: after + Math.random() * maxDelay,
        duration: minDuration + Math.random() * durationSpread,
        size: 8 + Math.random() * 10,
        round: i % 3 === 0,
        color: colors[i % colors.length],
      })),
    [count, maxDelay, minDuration, durationSpread, after],
  );

  return (
    <div className="fx-confetti">
      {bits.map((bit, i) => (
        <span
          key={i}
          className={`fx-confetti-bit${bit.round ? " round" : ""}`}
          style={{
            left: `${bit.left}%`,
            width: `${bit.size}px`,
            height: `${bit.size * (bit.round ? 1 : 0.45)}px`,
            background: bit.color,
            animationDelay: `${bit.delay}s`,
            animationDuration: `${bit.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

const RankUp = ({ name, position, from }) => (
  <div className="fx-rankup">
    <div className="fx-rankup-arrow">▲</div>
    <div className="fx-rankup-text">
      <div className="fx-rankup-name">{name}</div>
      <div className="fx-rankup-move">
        {from ? `van #${from} naar ` : "nu "}
        <b>#{position}</b>
      </div>
    </div>
  </div>
);

// Vuurwerk bij een gebroken record; de knallen langs de randen, het midden is voor de tekst.
const burstSpots = [
  { x: 14, y: 20, color: "#ffd166", delay: 0 },
  { x: 84, y: 16, color: "#7fd8ff", delay: 0.15 },
  { x: 30, y: 44, color: "#ff9a3c", delay: 0.3 },
  { x: 70, y: 38, color: "#ffffff", delay: 0.45 },
  { x: 8, y: 62, color: "#cfeaff", delay: 0.6 },
  { x: 92, y: 56, color: "#ffd166", delay: 0.75 },
  { x: 46, y: 12, color: "#7fd8ff", delay: 0.95 },
  { x: 22, y: 82, color: "#ff9a3c", delay: 1.15 },
  { x: 78, y: 84, color: "#ffffff", delay: 1.35 },
  { x: 58, y: 66, color: "#ffd166", delay: 1.55 },
  { x: 36, y: 26, color: "#cfeaff", delay: 1.8 },
  { x: 66, y: 20, color: "#ff9a3c", delay: 2 },
  { x: 12, y: 40, color: "#ffffff", delay: 2.2 },
  { x: 88, y: 34, color: "#7fd8ff", delay: 2.4 },
  { x: 42, y: 86, color: "#ffd166", delay: 2.6 },
  { x: 62, y: 46, color: "#ff9a3c", delay: 2.85 },
  { x: 26, y: 62, color: "#7fd8ff", delay: 3.05 },
  { x: 74, y: 70, color: "#cfeaff", delay: 3.25 },
];

// Platte vonken zonder gloed: een blur per vonk trekt de Pi niet.
const Fireworks = ({ name, amount, service, previous }) => {
  const bursts = useMemo(() => {
    const fx = fxSettings();
    // Een kern en een buitenring; op de laagste trap alleen de buitenring.
    const rings = fx.burstRings > 1 ? [0.55, 1] : [1];

    return thin(burstSpots, fx.burstSpots).map((burst) => {
      const count = fx.burstBits + Math.floor(Math.random() * 8);
      const bits = rings.flatMap((ring, r) =>
        spread(count, (i) => {
          const angle = ((i + r * 0.5) / count) * Math.PI * 2;
          const distance = (140 + Math.random() * 150) * ring;
          return {
            tx: Math.cos(angle) * distance,
            ty: Math.sin(angle) * distance,
            size: (11 + Math.random() * 9) * (rings.length > 1 && !r ? 0.7 : 1),
          };
        }),
      );
      return { ...burst, bits };
    });
  }, []);

  return (
    <div className="fx-fireworks">
      <div className="fx-fireworks-dim" />

      {bursts.map((burst, i) => (
        <div
          key={i}
          className="fx-burst"
          style={{ left: `${burst.x}%`, top: `${burst.y}%` }}
        >
          {burst.bits.map((bit, j) => (
            <span
              key={j}
              className="fx-burst-bit"
              style={{
                background: burst.color,
                animationDelay: `${burst.delay}s`,
                width: `${bit.size}px`,
                height: `${bit.size}px`,
                margin: `${-bit.size / 2}px 0 0 ${-bit.size / 2}px`,
                "--tx": `${bit.tx}px`,
                "--ty": `${bit.ty}px`,
              }}
            />
          ))}
        </div>
      ))}

      {name ? (
        <div className="fx-record-wrap">
          <div className="fx-record">
            <div className="fx-record-kicker">Nieuw record</div>
            <div className="fx-record-title">GROOTSTE VERKOOP OOIT</div>
            <div className="fx-record-name">{name}</div>
            <div className="fx-record-amount">€{formatCurrency(amount)}</div>
            {service ? <div className="fx-record-service">{service}</div> : null}
            {previous ? (
              <div className="fx-record-previous">
                oude record €{formatCurrency(previous)}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Na een verkoop (continued) alleen geluid, los afgespeeld meteen de aankondiging als record.
const PersonalBest = ({ continued, name, amount, service, previous }) =>
  continued ? null : (
    <SaleSplash
      name={name}
      amount={amount}
      service={service}
      ms={personalBestMs}
      best={{ previous, at: 0 }}
    />
  );

// De aanloop naar het target. De vlucht uit de hero is een FLIP: de meter
// staat op zijn eindplek en begint op de gemeten plek en maat van de
// hero-meter. Wijzer, boog en getal worden per frame via refs gezet, zonder
// React; de rest doet de CSS met de momenten als variabelen.
const tcSize = 760;
const tcStroke = 44;

// Bij Film (is-epic) als in een trailer: balken, de meter die langzaam naar je toe komt, en
// op 100 een schok (de scherven zijn charge-burst). De klap zit in target-aanloop.wav.
const TargetCharge = () => {
  const epic = !isPlainAlert();
  const geometry = useMemo(() => gaugeGeometry(tcSize, tcStroke, 0), []);
  const flyRef = useRef(null);
  const arcRef = useRef(null);
  const needleRef = useRef(null);
  const numberRef = useRef(null);

  // Meet de hero-meter, terug geschaald naar de 1920x1080 van ScaledCanvas.
  useLayoutEffect(() => {
    const fly = flyRef.current;
    const layer = fly?.closest(".fx-layer");
    const hero = fly
      ?.closest(".dashboard-container")
      ?.querySelector(".hero-gauge svg");
    if (!fly || !layer || !hero || !layer.offsetWidth) return;

    const scale = layer.getBoundingClientRect().width / layer.offsetWidth;
    const from = hero.getBoundingClientRect();
    // Meten zonder de transform van de vlucht; we staan nog op het eerste beeld.
    fly.style.animation = "none";
    const to = fly.getBoundingClientRect();
    fly.style.animation = "";
    if (!from.width || !to.width) return;

    const dx = (from.left + from.width / 2 - (to.left + to.width / 2)) / scale;
    const dy = (from.top + from.height / 2 - (to.top + to.height / 2)) / scale;
    fly.style.setProperty("--fly-x", `${dx}px`);
    fly.style.setProperty("--fly-y", `${dy}px`);
    fly.style.setProperty("--fly-s", `${from.width / to.width}`);
  }, []);

  useEffect(() => {
    const begin = performance.now();
    let frame = 0;

    const draw = (now) => {
      const part = targetSweepAt(now - begin);
      const arc = arcRef.current;
      if (arc) {
        arc.style.strokeDashoffset = `${geometry.length * (1 - part)}`;
        arc.style.stroke = heatColor(part * 100);
        // Een boog van lengte nul tekent met zijn ronde uiteinde nog een stip.
        arc.style.opacity = part > 0.004 ? "1" : "0";
      }
      if (needleRef.current) {
        needleRef.current.style.transform = `rotate(${180 * part}deg)`;
      }
      if (numberRef.current) {
        numberRef.current.textContent = `${Math.round(part * 100)}%`;
      }
      // Op tijd stoppen, niet op stand 1: daar vertrekt hij ook.
      if (now - begin < targetFullMs) {
        frame = requestAnimationFrame(draw);
      } else if (arc) {
        // Op 100 wit, tegelijk met de flits.
        arc.style.stroke = "url(#fxTcReached)";
      }
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [geometry]);

  const { cx, cy, radius, height, path, length, ticks } = geometry;

  return (
    <div
      className={epic ? "fx-tc is-epic" : "fx-tc"}
      style={{
        "--fx-tc-start": `${targetSweepStartMs}ms`,
        "--fx-tc-full": `${targetFullMs}ms`,
        "--fx-tc-fly": `${targetFlyMs}ms`,
        "--fx-tc-ms": `${targetChargeMs}ms`,
      }}
    >
      <div className="fx-tc-dim" />
      {epic && (
        <>
          <div className="fx-charge-bar is-top" />
          <div className="fx-charge-bar is-bottom" />
        </>
      )}
      <div className="fx-tc-shake">
        <div className="fx-tc-push">
          <div className="fx-tc-card">
            <div className="fx-tc-pop">
              <div className="fx-tc-kicker">Team target</div>
              <div ref={flyRef} className="fx-tc-fly">
                <svg
                  className="fx-tc-gauge"
                  width={tcSize}
                  height={height}
                  viewBox={`0 0 ${tcSize} ${height}`}
                >
                  <defs>
                    <linearGradient id="fxTcReached" x1="0" y1="0" x2="1" y2="0">
                      {reachedColors.map((color, index) => (
                        <stop
                          key={color}
                          offset={index / (reachedColors.length - 1)}
                          stopColor={color}
                        />
                      ))}
                    </linearGradient>
                  </defs>
                  {ticks.map((tick, i) => (
                    <line
                      key={i}
                      x1={tick.x1}
                      y1={tick.y1}
                      x2={tick.x2}
                      y2={tick.y2}
                      stroke={tick.major ? "rgba(255, 255, 255, 0.55)" : "rgba(255, 255, 255, 0.25)"}
                      strokeWidth={tick.major ? 4 : 2}
                      strokeLinecap="round"
                    />
                  ))}
                  <path
                    d={path}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.12)"
                    strokeWidth={tcStroke}
                    strokeLinecap="round"
                  />
                  <path
                    ref={arcRef}
                    d={path}
                    fill="none"
                    stroke={heatColor(100)}
                    strokeWidth={tcStroke}
                    strokeLinecap="round"
                    style={{ strokeDasharray: `${length} ${length}`, strokeDashoffset: 0 }}
                  />
                  <g
                    ref={needleRef}
                    style={{
                      transformBox: "view-box",
                      transformOrigin: `${cx}px ${cy}px`,
                      transform: "rotate(180deg)",
                    }}
                  >
                    <line
                      x1={cx}
                      y1={cy}
                      x2={cx - radius + tcStroke}
                      y2={cy}
                      stroke="#ffffff"
                      strokeWidth={10}
                      strokeLinecap="round"
                    />
                  </g>
                  <circle cx={cx} cy={cy} r={22} fill="#ffffff" />
                </svg>
              </div>
              <div ref={numberRef} className="fx-tc-number">
                100%
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="fx-tc-flash" />
    </div>
  );
};

const TargetHit = ({ amount }) => (
  <div className="fx-target">
    <ConfettiBurst />
    <div className="fx-target-card">
      <div className="fx-target-kicker">Team target</div>
      <div className="fx-target-title">GEHAALD</div>
      {amount ? (
        <div className="fx-target-amount">€{formatCurrency(amount)}</div>
      ) : null}
    </div>
  </div>
);

// De scherven als een aanloop vol is (charge-burst): een eigen laag boven de aankondiging,
// zodat ze helemaal uitvliegen terwijl de aanloop zelf al weg is. Ze vertrekken waar de
// rij blokjes of de meter op dat moment staat.
const burstFrom = {
  cells: { selector: ".fx-charge-cells", width: 520 },
  meter: { selector: ".fx-tc-fly", width: 60 },
};

const ChargeBurst = ({ big = false, from = "cells" }) => {
  const rootRef = useRef(null);
  const source = burstFrom[from] || burstFrom.cells;
  const shards = useMemo(() => chargeShards(big, source.width), [big, source]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const layer = root?.closest(".fx-layer");
    const anchor = layer?.querySelector(source.selector);
    if (!root || !layer || !anchor || !layer.offsetWidth) return;

    const scale = layer.getBoundingClientRect().width / layer.offsetWidth;
    const box = layer.getBoundingClientRect();
    const at = anchor.getBoundingClientRect();
    root.style.setProperty("--fx-bx", `${(at.left + at.width / 2 - box.left) / scale}px`);
    root.style.setProperty("--fx-by", `${(at.top + at.height / 2 - box.top) / scale}px`);
  }, [source]);

  return (
    <div ref={rootRef} className="fx-shatter">
      {shards.map((style, i) => (
        <i key={i} style={style} />
      ))}
    </div>
  );
};

// Geld naar de meter (./targetFeed.js): het bedrag springt op in het midden en vliegt in
// een boog de teammeter in, met een staart van blokjes; bij de landing vonken. De boog is
// twee lagen: de ene schuift opzij, de andere omhoog, elk met een eigen versnelling.
// De staart: elk blokje 60ms later; hoeveel er zijn hangt af van de kwaliteitstrap.
const feedTrailGap = 60;

const feedSparks = spread(12, (i) => {
  const angle = (i / 12) * Math.PI * 2;
  const reach = 70 + (i % 3) * 30;
  return {
    background: heatColor(30 + (i % 4) * 20),
    "--x": `${Math.cos(angle) * reach}px`,
    "--y": `${Math.sin(angle) * reach}px`,
  };
});

const TargetFeed = ({ amount = 0 }) => {
  const rootRef = useRef(null);
  const trail = spread(fxSettings().feedTrail, (i) => 70 + i * feedTrailGap);

  // Meet de hero-meter, terug geschaald naar de 1920x1080 van ScaledCanvas.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const layer = root?.closest(".fx-layer");
    const hero = root?.closest(".dashboard-container")?.querySelector(".hero-gauge svg");
    if (!root || !layer || !hero || !layer.offsetWidth) return;

    const scale = layer.getBoundingClientRect().width / layer.offsetWidth;
    const box = layer.getBoundingClientRect();
    const gauge = hero.getBoundingClientRect();
    const dx = (gauge.left + gauge.width / 2 - (box.left + box.width / 2)) / scale;
    const dy = (gauge.top + gauge.height / 2 - (box.top + box.height / 2)) / scale;
    root.style.setProperty("--fx-dx", `${dx}px`);
    root.style.setProperty("--fx-dy", `${dy}px`);
  }, []);

  const flight = { animationDelay: `${feedPopMs}ms`, animationDuration: `${feedLandMs - feedPopMs}ms` };
  const flying = (content, extra = 0, key) => (
    <div
      key={key}
      className="fx-feed-x"
      style={{ ...flight, animationDelay: `${feedPopMs + extra}ms` }}
    >
      <div className="fx-feed-y" style={{ ...flight, animationDelay: `${feedPopMs + extra}ms` }}>
        {content}
      </div>
    </div>
  );

  return (
    <div ref={rootRef} className="fx-feed" style={{ "--fx-land": `${feedLandMs}ms` }}>
      {trail.map((extra, i) =>
        flying(
          <i
            className="fx-feed-bit"
            style={{
              background: heatColor(40 + i * 12),
              animationDelay: `${feedPopMs + extra}ms`,
              animationDuration: `${feedLandMs - feedPopMs}ms`,
            }}
          />,
          extra,
          i,
        ),
      )}
      {flying(<div className="fx-feed-coin">€{formatCurrency(amount)}</div>, 0, "coin")}
      <div className="fx-feed-sparks">
        {feedSparks.map((style, i) => (
          <i key={i} style={style} />
        ))}
      </div>
    </div>
  );
};

// Een tikje donkerder tijdens het targetfeest, zodat lichtshow en confetti loskomen.
const TargetPartyDim = () => (
  <div className="fx-target-dim" style={{ "--fx-target-dim-ms": `${targetPartyMs}ms` }} />
);

// Naast de rand, niet erin: de rand knippert en zou de toeters meenemen.
// Het oude seintje (Oud in het lab) is alleen de rand, in zijn eigen ritme.
const ScreenGlow = () =>
  isPlainAlert() ? (
    <div className="fx-glow is-plain" />
  ) : (
    <>
      <div className="fx-glow" />
      <AirHorns />
    </>
  );

// De omroeper: een band met waarschuwingstape over het midden; de tweede regel komt
// als hij hem uitspreekt, en op de slag na de zin veert de kop (./horn.js).
const attentionStyle = {
  "--fx-attn-ms": `${voiceMs}ms`,
  "--fx-attn-line": `${voiceLineMs}ms`,
  "--fx-attn-hit": `${voiceEndMs + 80}ms`,
};

const SaleAttention = () => (
  <div className="fx-attn" style={attentionStyle}>
    <div className="fx-attn-band">
      <div className="fx-attn-tape" />
      <div className="fx-attn-title">ATTENTIE!</div>
      <div className="fx-attn-line">We hebben een nieuwe verkoop!</div>
      <div className="fx-attn-tape" />
    </div>
  </div>
);

// Hoe laat het scherm uitging: "gisteren 17:42".
function sinceLabel(since) {
  if (!since) return "";
  const then = dayjs(since).locale("nl");
  const today = dayjs();
  const time = then.format("HH:mm");
  if (then.isSame(today, "day")) return `vandaag ${time}`;
  if (then.isSame(today.subtract(1, "day"), "day")) return `gisteren ${time}`;
  return `${then.format("dddd D MMMM")} ${time}`;
}

// Hoe lang het daarna donker was, grof afgerond: "14 uur".
function darkLabel(since) {
  if (!since) return "";

  const minutes = Math.max(1, Math.round((Date.now() - since) / 60000));
  if (minutes < 60) return `${minutes} minuten`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} uur`;

  const days = Math.round(hours / 24);
  return days === 1 ? "een dag" : `${days} dagen`;
}

// Past de lijst niet, dan worden de kleinste verkopen samen één laatste regel.
function missedRows(sales) {
  if (sales.length <= maxRows) return sales;

  const rest = sales.slice(maxRows - 1);
  return [
    ...sales.slice(0, maxRows - 1),
    {
      name: `en nog ${rest.length} verkopen`,
      amount: rest.reduce((sum, sale) => sum + sale.amount, 0),
      service: null,
      rest: true,
    },
  ];
}

// Wat er verkocht is terwijl de tv uit stond, regel voor regel en dan het
// totaal. Zonder verkopen komt de kaart ook, als "NIKS GEMIST".
const MissedSales = ({ since, amount, sales = [] }) => {
  const rows = missedRows(sales);
  const empty = rows.length === 0;
  const totalMs = (empty ? 0 : rowDelayMs(rows.length - 1)) + totalAfterMs;

  return (
    <div
      className={`fx-missed${empty ? " is-empty" : ""}`}
      style={{ "--fx-missed-ms": `${missedSalesMs(sales.length)}ms` }}
    >
      {!empty && <ConfettiBurst after={totalMs / 1000} maxDelay={1.2} />}

      <div className="fx-missed-card">
        <div className="fx-missed-line fx-missed-kicker">
          {empty ? "Welkom terug" : "Even inhalen"}
        </div>
        <div className="fx-missed-line fx-missed-title">
          {empty ? "NIKS GEMIST" : "VERKOPEN GEMIST"}
        </div>
        <div className="fx-missed-line fx-missed-since">
          Laatste keer bekeken: {sinceLabel(since)}
          <span className="fx-missed-dot">·</span>
          {darkLabel(since)} donker
        </div>

        {empty && (
          <div
            className="fx-missed-line fx-missed-empty"
            style={{ animationDelay: `${rowDelayMs(0)}ms` }}
          >
            <div className="fx-missed-empty-text">
              Helaas, er is niks verkocht
            </div>
            <div className="fx-missed-empty-note">Op naar de eerste van vandaag</div>
          </div>
        )}

        <ol className="fx-missed-list">
          {rows.map((sale, index) => (
            <li
              key={`${sale.name}-${index}`}
              className={`fx-missed-line fx-missed-row${sale.rest ? " is-rest" : ""}`}
              style={{ animationDelay: `${rowDelayMs(index)}ms` }}
            >
              <span className="fx-missed-row-index">{sale.rest ? "+" : index + 1}</span>
              <span className="fx-missed-row-who">
                <span className="fx-missed-row-name">{sale.name}</span>
                {sale.service && (
                  <span className="fx-missed-row-service">{sale.service}</span>
                )}
              </span>
              <span className="fx-missed-row-amount">€{formatCurrency(sale.amount)}</span>
            </li>
          ))}
        </ol>

        {!empty && (
          <div
            className="fx-missed-line fx-missed-total"
            style={{ animationDelay: `${totalMs}ms` }}
          >
            Samen <b>€{formatCurrency(amount)}</b> erbij
          </div>
        )}
      </div>
    </div>
  );
};

// Banen kleur in één strook van twee schermen breed met het patroon twee
// keer: hij schuift één scherm op en loopt zo naadloos rond, met maar één
// bewegend element.
const bandColors = ["#ffd166", "#ff9a3c", "#7fd8ff", "#cfeaff", "#ffffff"];

const PartyBlocks = () => {
  const { partyBands } = fxSettings();
  const bands = spread(partyBands * 2, (i) => ({
    // Eén scherm is 50% van de strook, met partyBands banen erin.
    left: `${(i * 50) / partyBands}%`,
    color: bandColors[i % bandColors.length],
  }));

  return (
    <div
      className="fx-party-blocks"
      style={{ "--fx-party-blocks-ms": `${partyBlocksMs}ms` }}
    >
      <div className="fx-party-strip">
        {bands.map((band, i) => (
          <i key={i} style={{ left: band.left, background: band.color }} />
        ))}
      </div>
    </div>
  );
};

// De balk onderin die de regels doorbladert; het target geeft eigen regels mee.
const PartyHype = ({ lines = partyLines }) => (
  <div
    className="fx-party-hype"
    style={{ "--fx-party-hype-ms": `${lines.length * partyLineMs}ms` }}
  >
    <div className="fx-party-hype-bar">
      {lines.map((line, index) => (
        <span
          key={line}
          className="fx-party-hype-line"
          style={{
            animationDelay: `${index * partyLineMs}ms`,
            animationDuration: `${partyLineMs}ms`,
          }}
        >
          {line}
        </span>
      ))}
    </div>
  </div>
);

// De lichtshow: vlakke kegels (clip-path) die om hun top draaien, om en om
// de andere kant op en elk in een eigen tempo.
const beamColors = ["#ffd166", "#7fd8ff", "#ff9a3c", "#cfeaff", "#ffd166", "#7fd8ff"];

const PartyLights = ({ ms = partyLightsMs }) => {
  const { partyBeams } = fxSettings();
  const beams = spread(partyBeams, (i) => ({
    // Verdeeld over 8 tot 92% van de breedte.
    left: `${8 + (i * 84) / Math.max(1, partyBeams - 1)}%`,
    color: beamColors[i % beamColors.length],
    reverse: i % 2 === 1,
    beats: 4 + (i % 3),
  }));

  return (
    <div
      className="fx-party-lights"
      style={{ "--fx-party-lights-ms": `${ms}ms` }}
    >
      {beams.map((beam, i) => (
        <i
          key={i}
          className={`fx-party-beam${beam.reverse ? " is-reverse" : ""}`}
          style={{
            left: beam.left,
            background: beam.color,
            "--fx-beam-beats": beam.beats,
          }}
        />
      ))}
    </div>
  );
};

// Confetti uit beide hoeken onderin. Elke snipper is twee elementen met een
// eigen tijdverloop: de buitenste schuift opzij, de binnenste gaat op en neer
// en draait. Samen maken ze de boog.
const PartyCannons = () => {
  const bits = useMemo(() => {
    const perSide = fxSettings().partyCannonBits;
    return ["left", "right"].flatMap((side) =>
      spread(perSide, (i) => ({
        side,
        // Opzij en omhoog in px; samen de hoek.
        dx: (side === "left" ? 1 : -1) * (260 + Math.random() * 820),
        up: -(460 + Math.random() * 480),
        // Hoe ver hij vanaf het hoogste punt zakt.
        drop: 420 + Math.random() * 520,
        spin: (Math.random() * 2 - 1) * 900,
        delay: Math.random() * partyCannonSpread,
        duration: 2.2 + Math.random(),
        size: 9 + Math.random() * 10,
        round: i % 3 === 0,
        color: colors[i % colors.length],
      })),
    );
  }, []);

  return (
    <div className="fx-party-cannons">
      {bits.map((bit, i) => (
        <span
          key={i}
          className={`fx-cannon-bit is-${bit.side}`}
          style={{
            "--dx": `${bit.dx}px`,
            "--up": `${bit.up}px`,
            "--drop": `${bit.drop}px`,
            "--spin": `${bit.spin}deg`,
            animationDelay: `${bit.delay}s`,
            animationDuration: `${bit.duration}s`,
          }}
        >
          <i
            className={bit.round ? "round" : ""}
            style={{
              width: `${bit.size}px`,
              height: `${bit.size * (bit.round ? 1 : 0.45)}px`,
              background: bit.color,
              animationDelay: `${bit.delay}s`,
              animationDuration: `${bit.duration}s`,
            }}
          />
        </span>
      ))}
    </div>
  );
};

// De stempel die bij een record over de aankondiging slaat.
const RecordStamp = ({ kind = "best" }) => (
  <div className="fx-stamp">
    <div className="fx-stamp-flash" />
    <div className="fx-stamp-mark">
      <div className="fx-stamp-kicker">
        {kind === "record" ? "Grootste verkoop ooit" : "Grootste verkoop tot nu toe"}
      </div>
      <div className="fx-stamp-text">
        {kind === "record" ? "NIEUW RECORD!" : "EIGEN RECORD!"}
      </div>
    </div>
  </div>
);

const effects = {
  "sale-charge": SaleCharge,
  "charge-burst": ChargeBurst,
  "sale-splash": SaleSplash,
  "confetti-burst": ConfettiBurst,
  "record-stamp": RecordStamp,
  "rank-up": RankUp,
  "screen-glow": ScreenGlow,
  "sale-attention": SaleAttention,
  fireworks: Fireworks,
  "personal-best": PersonalBest,
  "target-feed": TargetFeed,
  "target-charge": TargetCharge,
  "target-hit": TargetHit,
  "target-party-dim": TargetPartyDim,
  "missed-sales": MissedSales,
  "missed-nothing": MissedSales,
  "party-blocks": PartyBlocks,
  "party-hype": PartyHype,
  "party-lights": PartyLights,
  "party-cannons": PartyCannons,
  // party-dance loopt via een klasse op de dashboard-div.
};

const AnimationLayer = ({ active }) => (
  <div className="fx-layer" aria-hidden="true">
    {active.map((run) => {
      const Effect = effects[run.id];
      return Effect ? <Effect key={run.runId} {...run.payload} /> : null;
    })}
  </div>
);

export default AnimationLayer;
